import { Box  } from "@mui/material";
import { useContext, useEffect, useReducer, useRef, useState } from "react";

import { getWorkOrderDetails, postLiveningForm } from "../../../data/mahiApiConnections";
import { MahiContext } from "../../state/MahiContext";
import IcpInspectionStepLocation from "./formsteps/IcpInspectionStepLocation";
import IcpInspectionStepDetails from "./formsteps/IcpInspectionStepDetails";
import FormStepper from "./formsteps/FormStepper";
import IcpInspectionStepSummary from "./formsteps/IcpInspectionStepSummary";
import { hikoAlert } from "../../../utils/HikoAlert";
import IcpInspectionStepComments from "./formsteps/IcpInspectionStepComments";
import { formatISO } from "date-fns";
import IcpInspectionStepAsBuilt from "./formsteps/IcpInspectionStepAsBuilt";
import { DebugBox, LoadingBox } from "@northpowerdev/np-react-utils";
import IcpInspectionDetailsAccordion from "./IcpInspectionDetailsAccordion";

export default function IcpInspectionByWorkOrder() {

    const [pageLoading, setPageLoading]           = useState(false)
    const [icpNumber, setIcpNumber]               = useState()
    const [workOrderId, setWorkOrderId]           = useState()
    const [workOrderDetails, setWorkOrderDetails] = useState()
    const [formStep, setFormStep]                 = useState(0)
    const [sentToNorthpower, setSentToNorthpower] = useState(false)
    const [initialZoom, setInitialZoom]           = useState(17)
    const mahiContext = useContext(MahiContext)

    const formStepperRef = useRef()
    // const pageTitleRef = useRef()

    const initialFormState = {
        markerLatLng: {lat: -35.7464, lng: 174.3094},
        meterLocationDescription: '',
        serviceBranchConnection: '',
        connectionType: '', 
        connectionDate: null,
        fuseRating: '',
        fuseRatingOther: '',
        phaseList: [],
        rippleControllerPhase: '',
        phaseRotation: null,
        loopImpedanceOK: null,
        comments: '',
        asBuiltFileObjects: [], // E.g. [{fileKey: "...", url: "..."}, ...]
        loopImpedanceTestLocation: '',
        loopImpedanceProblemPhases: '',
        loopImpedanceReadings: '',
        loopImpedanceAtNetwork: '',
    }
    const [formState, dispatch] = useReducer(formStateReducer, initialFormState)

    const handleStepperBack = (event) => {
        // console.log('stepper previous button clicked')
        if (formStep === 0) {
            // We no longer show the back button at form step zero
            // nagivateToIcpInspection(event)
        } else {
            setFormStep(formStep - 1)
            if (formStepperRef?.current) {
                formStepperRef.current.scrollIntoView()
            }
        }
    }

    const handleStepperNext = () => {
        setFormStep(formStep + 1) // next from last step in form will end up displaying nothing
        if (formStepperRef?.current) {
            // console.log('formStepperRef.current', formStepperRef.current)
            formStepperRef.current.scrollIntoView()
        }
    }

    const handleStepperSendToNorthpower = async () => { 
        setPageLoading(true)
        try {
            const postData = convertToBackendFormat(icpNumber, workOrderId, formState)
            const submitResponse = await postLiveningForm(mahiContext, postData)
            console.log('submitResponse', submitResponse)
            hikoAlert('Thanks!', 'Your data has been received by Northpower', 'success')
            setSentToNorthpower(true) // postLiveningForm succeeds or throws error
        } catch (e) {
            console.error(e)
            hikoAlert('There was a problem', 'We could not send your new connection data to Northpower')
        } finally {
            setPageLoading(false)
        }
    }

    useEffect(() => {
        
        let didCancel = false;
        const abortController = new AbortController()

        const asyncGetWorkOrder = async (someWorkOrderId, paramIcpNumber, abortSignal) => {
            setPageLoading(true)
            try {
                const workOrderResponse = await getWorkOrderDetails(mahiContext, someWorkOrderId, abortSignal)
                if (!didCancel) {
                    setWorkOrderDetails(workOrderResponse)
                    if (workOrderResponse?.workOrder?.icpNo && workOrderResponse?.workOrder?.icpNo !== paramIcpNumber) {
                        setIcpNumber(workOrderResponse?.workOrder?.icpNo) // Replace 6 digit with 15 digit ICP Number
                    }
                    if (workOrderResponse?.workOrder?.location?.latitude) {
                        //
                        // We've been given a latitude in the work order. Assume we have a longitude as well.
                        // Use latitude and longitude to set the initial map pin location.
                        // Since we know the location, zoom the map in quite a bit closer.
                        //
                        const workOrderLatLng = {
                            lat: workOrderResponse?.workOrder?.location?.latitude*1,
                            lng: workOrderResponse?.workOrder?.location?.longitude*1}
                        dispatch({type: 'markerLatLng', value: workOrderLatLng})
                        setInitialZoom(17)
                    }
                }
            } catch {
                hikoAlert('No work order found', 'We could not find details for your work order')
            } finally {
                if (!didCancel) {
                    setPageLoading(false)
                }
            }
        }
        // console.log('IcpInspectionByWorkOrder useEffect window.location', window.location)
        const searchParams = new URLSearchParams(window.location.search)
        const paramIcpNumber = searchParams.get('icp_number');
        const paramWorkOrderId = searchParams.get('work_order_id');
        setIcpNumber(paramIcpNumber)
        setWorkOrderId(paramWorkOrderId)
        asyncGetWorkOrder(paramWorkOrderId, paramIcpNumber, abortController.signal)

        return (() => {didCancel = true})

    // lint expects mahiContext but this causes re-renders
    // eslint-disable-next-line
    }, []);

    const networkApprovedPhase = workOrderDetails?.workOrder?.networkApprovedParameters?.phase
    const networkApprovedPhaseList = []
    if (networkApprovedPhase) {
        if (networkApprovedPhase.red)   { networkApprovedPhaseList.push('Red') }
        if (networkApprovedPhase.white) { networkApprovedPhaseList.push('White') }
        if (networkApprovedPhase.blue)  { networkApprovedPhaseList.push('Blue') }
    }

    return (
        <Box className="Page">
            <IcpInspectionDetailsAccordion
                title="Submit ICP Inspection"
                workOrderId={workOrderId}
                icpNumber={icpNumber}
                workOrderDetails={workOrderDetails}
            />
           
            {
                workOrderDetails?.workOrder?.registryStatus !=='new'
                &&
                <>
                    <LoadingBox loading={pageLoading} message={'Loading...'}>
                    <div ref={formStepperRef}>&nbsp;</div>
                    <FormStepper currentStep={formStep}>
                        <IcpInspectionStepLocation formState={formState} dispatch={dispatch}                            onNext={handleStepperNext} address={workOrderDetails?.workOrder?.streetAddress} initialZoom={initialZoom} />
                        <IcpInspectionStepDetails  formState={formState} dispatch={dispatch} onBack={handleStepperBack} onNext={handleStepperNext} networkApprovedPhaseList={networkApprovedPhaseList} networkApprovedParameters={workOrderDetails?.workOrder?.networkApprovedParameters} />
                        <IcpInspectionStepComments formState={formState} dispatch={dispatch} onBack={handleStepperBack} onNext={handleStepperNext} networkApprovedPhaseList={networkApprovedPhaseList} networkApprovedParameters={workOrderDetails?.workOrder?.networkApprovedParameters} />
                        <IcpInspectionStepAsBuilt  formState={formState} dispatch={dispatch} onBack={handleStepperBack} onNext={handleStepperNext} workOrderId={workOrderId} networkApprovedPhaseList={networkApprovedPhaseList} />
                        <IcpInspectionStepSummary  formState={formState}                     onBack={handleStepperBack} onNext={handleStepperSendToNorthpower} sent={sentToNorthpower} />
                    </FormStepper>
                    </LoadingBox>
                </>
            }

            <DebugBox title="As Built File Keys">{JSON.stringify(formState.asBuiltFileObjects.map(fo => fo.fileKey))}</DebugBox>
            <DebugBox title="Work Order Details">{JSON.stringify(workOrderDetails)}</DebugBox>
        </Box>
    )
}


const updateFileObjects = (currentFileObjects, fileChangeSpecs) => {
    let changedFileObjects = [...currentFileObjects] // initialise by making a copy
    if (fileChangeSpecs?.length > 0) {
        console.log('updateFileObjects starting with changedFileObjects?.length', changedFileObjects?.length)
        let numAdded = 0, numRemoved = 0

        fileChangeSpecs.forEach(({action, file}) => {
            switch (action) {
                case 'Add':
                    console.log('updateFileObjects adding file.fileKey', file.fileKey)
                    changedFileObjects.push(file)
                    numAdded++
                    break;
                case 'Remove':
                    console.log('updateFileObjects filtering by file.fileKey', file.fileKey)
                    changedFileObjects = changedFileObjects.filter((c) => c.fileKey !== file.fileKey)
                    console.log('updateFileObjects after filter changedFileObjects', changedFileObjects)
                    numRemoved++
                    break;
                default:
                    console.error(`Unexpected action ${action} in as built status change`)
            }
        })
        console.log(`updateFileObjects ${numAdded} Added. ${numRemoved} Removed.`)
    }
    return changedFileObjects
}


const formStateReducer = (currentFormState, {type, value}) => {
    let newState
    switch (type) {

        case 'meterLocationDescription':
        case 'markerLatLng':
        case 'connectionDate':
        case 'serviceBranchConnection':
        case 'connectionType':
        case 'fuseRating':
        case 'fuseRatingOther':
        case 'rippleControllerPhase':
        case 'phaseRotation':
        case 'loopImpedanceOK':
        case 'comments':
        case 'loopImpedanceTestLocation':
        case 'loopImpedanceProblemPhases':
        case 'loopImpedanceReadings':
            newState = {...currentFormState}
            newState[type] = value
            break
        case 'loopImpedanceAtNetwork':
            newState = {...currentFormState}
            newState[type] = value
            break
        case 'phaseList': // Dispatch call must be e.g.: dispatch({type: 'phaseList', value: {phase: 'red', checked: 'true'}})
            // currentFormState.phaseList is e.g. ['red', 'white']
            let {phase, checked} = value
            let newPhaseList = currentFormState.phaseList.filter(p => p !== phase) // remove target phase from list
            if (checked) {
                newPhaseList = [phase, ...newPhaseList] // add target phase to the list
            }
            newState = {...currentFormState, phaseList: newPhaseList}
            break
        case 'asBuiltFileObjects':
            const fileChangeSpecs = value
            const newFileObjects = updateFileObjects(currentFormState.asBuiltFileObjects, fileChangeSpecs)
            newState = {...currentFormState, asBuiltFileObjects: newFileObjects}
            break
        default:
            hikoAlert('Problem updating ICP Inspection form', 'Inspection form did not recognise reducer action') // Mick Temp: should be hikoException so it goes to Sentry
            newState = currentFormState
            break
    }
    return newState
}

const convertToBackendFormat = (icpNumber, workOrderId, formData) => {

    let fuseRatingNumber
    switch (formData.fuseRating) {
        case "30 Amp":
            fuseRatingNumber = 30
            break
        case "60 Amp":
            fuseRatingNumber = 60
            break
        case "100 Amp":
            fuseRatingNumber = 100
            break
        case "Other":
            const fuseRatingFloat = parseFloat(formData.fuseRatingOther)
            if (isNaN(fuseRatingFloat)) {
                throw new Error('Fuse rating must be a number')
            }
            fuseRatingNumber = fuseRatingFloat
            break
        default:
            break
    }
    return {
        icpNumber: icpNumber,
        workOrderNumber: workOrderId,
        meterBoardLocation: {
            latitude:  formData.markerLatLng.lat,
            longitude: formData.markerLatLng.lng
        },
        meterBoardLocationDesc: formData.meterLocationDescription,
        connectionDate: formatISO(formData.connectionDate),
        phase: {
            blue:         formData.phaseList.includes('Blue'),
            red:          formData.phaseList.includes('Red'),
            white:        formData.phaseList.includes('White'),
            notTraceable: formData.phaseList.includes('Not Traceable')
        },
        rippleControllerPhase: {
            blue:         formData.rippleControllerPhase === 'Blue',
            red:          formData.rippleControllerPhase === 'Red',
            white:        formData.rippleControllerPhase === 'White',
            notTraceable: formData.rippleControllerPhase === 'Not Traceable',
            notInstalled: formData.rippleControllerPhase === 'Not Installed'
        },
        fuseRating: fuseRatingNumber,
        connectionPoint: formData.serviceBranchConnection,
        connectionType: formData.connectionType,
        isLoopImpedanceOK: formData.loopImpedanceOK === 'Yes',
        loopImpedanceIssues: (
            formData.loopImpedanceOK === 'Yes'
            ?
                null
            :
            {
                loopImpedanceTestLocation:  formData.loopImpedanceTestLocation,
                loopImpedanceProblemPhases: formData.loopImpedanceProblemPhases || null,
                loopImpedanceReadings:      formData.loopImpedanceReadings,
                loopImpedenceAtNetwork:     formData.loopImpedanceAtNetwork,
            }
        ),
        phaseRotation: (formData.phaseList.length === 3 ? formData.phaseRotation : null),
        comments: formData.comments,
        asBuiltFileKeys: formData.asBuiltFileObjects.map(f => f.fileKey),
    }
}